import users from '../../../services/data/users';
import debounce from 'lodash.debounce';

const resolveChannelName = ({ project, event, subject }) => `research.event_run.${project}.${event}.${subject}`;
const resolveGlobalChannelName = ({ project }) => `research.event_locks.${project}`;

function registerChannelListeners(channel, dispatch, commit, name = 'event_run') {
  return channel
    .listen(`.research.${name}.locked`, ({ event, subject, user, expires }) => {
      commit('setLock', { event, subject, lock: { user, expires } });

      if (user) {
        dispatch('loadUserData', { user });
      }
    })
    .listen(`.research.${name}.released`, ({ event, subject }) => {
      commit('setLock', { event, subject, lock: null });
    });
}

const lazyJoinChannel = debounce(async (name, dispatch, commit) => {
  if (!globalChannel) {
    let channel = (await lazyLoadEcho(name)).join(name).here((here) => {
      if (here && here.length && here[0]?.currentLocks) {
        const locks = here[0]?.currentLocks ?? [];

        locks.forEach(({ event, subject, ...lock }) => {
          commit('setLock', { event, subject, lock });
          if (lock?.user) {
            dispatch('loadUserData', { user: lock.user });
          }
        });
      }
    });

    globalChannel = registerChannelListeners(channel, dispatch, commit);
  }

  return globalChannel;
}, 100);

async function lazyLoadEcho() {
  return (await import('../../../services/broadcasting/echo')).default;
}

let globalChannel = null;

export const EventRunPresenceChannel = {
  namespaced: true,

  state: {
    locks: {},
    users: {},
  },

  getters: {
    isLockedForUser:
      ({ locks }) =>
      (user, event, subject) => {
        const lock = locks[subject]?.[event] ?? null;

        if (!lock) {
          return false;
        }

        const expirationDate = Date.parse(lock.expires);
        const now = Date.now();

        return lock.user !== user && now <= expirationDate;
      },

    userHasLock:
      ({ locks }) =>
      (user, event, subject) => {
        const lock = locks[subject]?.[event] ?? null;

        if (!lock) {
          return false;
        }

        const expirationDate = Date.parse(lock.expires);
        const now = Date.now();

        return lock.user === user && now <= expirationDate;
      },

    currentLockerUserId:
      ({ locks }) =>
      (event, subject) =>
        locks[subject]?.[event]?.user ?? null,

    currentLockingUser:
      ({ users }, { currentLockerUserId }) =>
      (event, subject) => {
        const userId = currentLockerUserId(event, subject);

        return users[userId] ?? null;
      },
  },

  mutations: {
    setLock(state, { event, subject, lock }) {
      state.locks = { ...state.locks, [subject]: { ...(state.locks[subject] ?? {}), [event]: lock } };
    },

    setUser(state, { userData }) {
      state.users = { ...state.users, [userData.id]: userData };
    },
  },

  actions: {
    async subscribeToChannel({ commit, dispatch }, { project, event, subject }) {
      const channelName = resolveChannelName({ project, event, subject });

      const Echo = await lazyLoadEcho();

      const channel = Echo.join(channelName).here((here) => {
        if (here && here.length && here[0]?.currentLock) {
          const lock = here[0]?.currentLock ?? null;
          commit('setLock', { event, subject, lock });

          if (lock?.user) {
            dispatch('loadUserData', { user: lock.user });
          }
        }
      });

      registerChannelListeners(channel, dispatch, commit);
    },

    async subscribeToGlobalChannel({ commit, dispatch, state }, { project }) {
      const channelName = resolveGlobalChannelName({ project });

      await lazyJoinChannel(channelName, dispatch, commit);
    },

    async leaveChannel(_store, { project, event, subject }) {
      const channelName = resolveChannelName({ project, event, subject });

      (await lazyLoadEcho()).leave(channelName);
    },

    async leaveGlobalChannel(_store, { project, event, subject }) {
      const channelName = resolveGlobalChannelName({ project, event, subject });

      (await lazyLoadEcho()).leave(channelName);
    },

    async loadCurrentLock({ commit, dispatch }, { repository, project, event, subject }) {
      const response = await this._vm.$api('repositories.projects.pipelines.events.run.getLock', {
        event,
        project,
        subject,
        repository,
      });

      const lock = response.data?.data ?? null;

      commit('setLock', { event, subject, lock });

      if (lock?.user) {
        dispatch('loadUserData', { user: lock.user });
      }
    },

    async adquireLock({ commit, dispatch }, { repository, project, event, subject, user }) {
      try {
        const response = await this._vm.$api('repositories.projects.pipelines.events.run.lock', {
          event,
          project,
          subject,
          repository,
        });

        const responseData = response?.data?.data ?? null;

        if (responseData) {
          commit('setLock', {
            event,
            subject,
            lock: {
              user,
              expires: responseData.expires,
            },
          });

          if (user) {
            dispatch('loadUserData', { user });
          }
        }
      } catch (err) {
        console.error(err);
      }
    },

    async releaseLock(_store, { repository, project, event, subject }) {
      await this._vm.$api('repositories.projects.pipelines.events.run.release', {
        event,
        project,
        subject,
        repository,
      });
    },

    async loadUserData({ commit }, { user }) {
      const userData = await users.get(user);

      commit('setUser', { userData });
    },
  },
};
